import { useState, useCallback, useEffect, useRef } from "react";
import { GetChecklistProgressUseCase } from "../../../features/month-end-checklist/domain";
import { ChecklistRepositoryImpl } from "../../../features/month-end-checklist/data/repositories/ChecklistRepositoryImpl";

const checklistRepository = new ChecklistRepositoryImpl();
const getChecklistProgressUseCase = new GetChecklistProgressUseCase(
  checklistRepository
);

/** Module-level cache: month key -> is closed (true) or not (false). No entry = not yet loaded. */
const monthClosedCache: Record<string, boolean> = {};

export interface UseIsMonthClosedReturn {
  /** True if the given month is closed (checklist status === "closed"). */
  isClosed: boolean | null;
  /** True while fetching. */
  loading: boolean;
  /** Error message if fetch failed. */
  error: string | null;
  /** Refetch for the current month. */
  refresh: () => Promise<void>;
}

/**
 * Determines if a month is closed using GET /api/month-end/checklists/progress?month=YYYY-MM.
 * Uses a module-level cache to avoid duplicate requests for the same month.
 *
 * @param month - "YYYY-MM" (e.g. "2025-01"). If undefined, returns { isClosed: null, loading: false }.
 */
export function useIsMonthClosed(
  month: string | undefined
): UseIsMonthClosedReturn {
  const [isClosed, setIsClosed] = useState<boolean | null>(() =>
    month !== undefined && month in monthClosedCache
      ? monthClosedCache[month]!
      : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchClosed = useCallback(async (targetMonth: string) => {
    if (targetMonth in monthClosedCache) {
      setIsClosed(monthClosedCache[targetMonth]!);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const progress = await getChecklistProgressUseCase.execute(targetMonth);
      const closed = progress.status === "closed";
      monthClosedCache[targetMonth] = closed;
      if (mountedRef.current) {
        setIsClosed(closed);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to load month status";
      if (mountedRef.current) {
        setError(message);
        setIsClosed(null);
      }
      if (
        typeof (err as { status?: number })?.status === "number" &&
        (err as { status: number }).status === 404
      ) {
        monthClosedCache[targetMonth] = false;
        if (mountedRef.current) {
          setIsClosed(false);
          setError(null);
        }
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    if (month) {
      delete monthClosedCache[month];
      await fetchClosed(month);
    }
  }, [month, fetchClosed]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (month === undefined) {
      setIsClosed(null);
      setLoading(false);
      setError(null);
      return;
    }
    fetchClosed(month);
  }, [month, fetchClosed]);

  return {
    isClosed: month === undefined ? null : isClosed,
    loading,
    error,
    refresh,
  };
}
